(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-peguu-measure-guide]";
  const MODAL_SELECTOR = "[data-peguu-measure-guide-modal]";
  const OPEN_SELECTOR = "[data-peguu-measure-guide-open]";
  const CLOSE_SELECTOR = "[data-peguu-measure-guide-close]";

  class PeguuMeasureGuide {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector(MODAL_SELECTOR);
      this.dialog = root.querySelector("[data-peguu-measure-guide-dialog]");
      this.closeButtons = root.querySelectorAll(CLOSE_SELECTOR);
      this.lastFocusedElement = null;
      this.isOpen = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);

      this.init();
    }

    init() {
      document.addEventListener("click", this.handleDocumentClick);
      document.addEventListener("keydown", this.handleKeydown);

      this.closeButtons.forEach((button) => {
        button.addEventListener("click", this.close);
      });
    }

    destroy() {
      document.removeEventListener("click", this.handleDocumentClick);
      document.removeEventListener("keydown", this.handleKeydown);

      this.closeButtons.forEach((button) => {
        button.removeEventListener("click", this.close);
      });
    }

    handleDocumentClick(event) {
      if (!this.root.isConnected) {
        return;
      }

      const opener = event.target.closest(OPEN_SELECTOR);

      if (!opener) {
        return;
      }

      event.preventDefault();
      this.open(opener);
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === "Escape") {
        this.close();
        return;
      }

      if (event.key === "Tab") {
        this.trapFocus(event);
      }
    }

    open(opener) {
      if (this.isOpen || !this.modal) {
        return;
      }

      this.isOpen = true;
      this.lastFocusedElement = opener || document.activeElement;

      this.modal.classList.add("is-open");
      this.modal.setAttribute("aria-hidden", "false");

      document.documentElement.classList.add("peguu-measure-guide-open");
      document.body.classList.add("peguu-measure-guide-open");

      window.requestAnimationFrame(() => {
        this.dialog?.focus();
      });
    }

    close() {
      if (!this.isOpen || !this.modal) {
        return;
      }

      this.isOpen = false;

      this.modal.classList.remove("is-open");
      this.modal.setAttribute("aria-hidden", "true");

      document.documentElement.classList.remove("peguu-measure-guide-open");
      document.body.classList.remove("peguu-measure-guide-open");

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus === "function"
      ) {
        this.lastFocusedElement.focus();
      }
    }

    trapFocus(event) {
      const focusableElements = this.modal.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          '[tabindex]:not([tabindex="-1"])'
        ].join(",")
      );

      const elements = Array.from(focusableElements).filter(
        (element) => element.offsetParent !== null
      );

      if (!elements.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  const initialize = () => {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!root || root.dataset.peguuInitialized === "true") {
      return;
    }

    if (
      window.PeguuMeasureGuide &&
      typeof window.PeguuMeasureGuide.destroy === "function"
    ) {
      window.PeguuMeasureGuide.destroy();
    }

    root.dataset.peguuInitialized = "true";
    window.PeguuMeasureGuide = new PeguuMeasureGuide(root);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", initialize);
})();
